import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router';
import { Box, Toolbar, Grid, Typography, Card, CardContent, useTheme, Chip, CardHeader, Container, Divider, IconButton, Hidden, TableBody, TableCell, TableHead, TableRow, Alert, Stack, Badge, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import ErrorIcon from '@mui/icons-material/Error';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NotificationsIcon from '@mui/icons-material/Notifications';
import HotelIcon from '@mui/icons-material/Hotel';
import NumbersIcon from '@mui/icons-material/Numbers';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import CancelIcon from '@mui/icons-material/Cancel';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import DescriptionIcon from '@mui/icons-material/Description';
import { showAlert as showGlobalAlert, clearAllAlerts as clearAllGlobalAlerts } from '../store/actions/alertActions';
import { RootState } from '../store/store';
import DateRangeSelector from '../components/DateRangeSelector';
import { hide, show } from '../store/actions/busyIndicatorActions';
import MobileDataList from '../components/MobileDataList';
import MobileShiftCard from '../components/shift/MobileShiftCard';
import Table from '../components/Table';
import useStatesFilter from '../components/StatesFilterDropdown';
import { changeDateRange, changeFilterByColumnDashboard, changeWeekDateRange, refreshDataWithDateRange, searchByTextDashboard } from '../store/actions/shiftActions';
import FormattedShift from '../models/FormattedShift';
import { formatDateToCalendarTime, formatDateToLongDate, formatDateToOnlyDate, formatDateToOnlyTime, formatErrorMessage } from '../utils/common';
import { changeDateRange as changeDateRangeNr, refreshData as refreshDataNr } from '../store/actions/dashboardNRActions';
import GlobalSearch from '../components/GlobalSearch';
import FormattedShiftNR from '../models/FormattedShiftNR';
import ShiftsService from '../services/ShiftsService';
import { getCheckinStatus, getIconContainerStyle, getIconColor, getPrestartStatus, getLocationStatus, getBreakStatus, iconStyle } from '../utils/complianceUtils';

interface Props {
  visibleRows: Array<FormattedShift>;
  visibleNrRows: Array<FormattedShiftNR>;
  startDate: string;
  endDate: string;
  onDateRangeChange: any;
  showBusyIndicator: any;
  hideBusyIndicator: any;
  fetchAllRows: any;
  companyId: number | undefined;
  searchedText: string,
  filterByText: any,
  filterByCol: any,
  onChangeFilterByColumn: any,
  showAlert: (message: any) => any,
  clearAllAlerts: () => any
}

const DashboardFatigue: React.FC<Props> = ({
  visibleRows,
  visibleNrRows,
  startDate,
  endDate,
  onDateRangeChange,
  showBusyIndicator,
  hideBusyIndicator,
  fetchAllRows,
  companyId,
  searchedText,
  filterByText,
  filterByCol,
  onChangeFilterByColumn,
  showAlert,
  clearAllAlerts
}) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [sortModel, setSortModel] = useState([]);
  const [selectedSummary, setSelectedSummary] = useState<string>('');
  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [testData, setTestData] = useState<any>(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const { StatesDropdown, selectedStates } = useStatesFilter();

  useEffect(() => {
    fetchAllRows(startDate, endDate);
  }, []);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const json = JSON.parse((e.target?.result)?.toString() || '');
          console.log(json.items);
          if (json.items) {
            setTestData(json.items.map((shift: any) => new FormattedShift(shift, [
              {
                id: 0,
                value: 'New'
              },
              {
                id: 1,
                value: 'Scheduled'
              },
              {
                id: 2,
                value: 'Ongoing'
              },
              {
                id: 3,
                value: 'Completed'
              },
              {
                id: 4,
                value: 'CompletedSmsOnly'
              }
            ])));
          }
          showAlert({ message: 'Test data loaded successfully', severity: 'success' });
          setShowUploadDialog(false);
        } catch (error) {
          showAlert({ message: 'Invalid JSON file', severity: 'error' });
        }
      };
      reader.readAsText(file);
    }
  };

  const clearTestData = () => {
    setTestData(null);
    showAlert({ message: 'Test data cleared', severity: 'success' });
  };

  // Add keyboard shortcut handler
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Check for Ctrl+U
      if (e.ctrlKey && e.key === 'u') {
        e.preventDefault(); // Prevent the browser's default behavior
        if (process.env.NODE_ENV === 'development') {
          if (testData) {
            clearTestData();
          } else {
            setShowUploadDialog(true);
          }
        }
      }
    };

    // Add event listener
    window.addEventListener('keydown', handleKeyPress);

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [testData]); // Depend on testData to re-attach listener when it changes

  // Filter visibleRows and visibleNrRows by selected states
  const filteredVisibleRows: FormattedShift[] = testData || (selectedStates && selectedStates.length > 0
    ? visibleRows.filter((x) => {
      const id = x?.jobsite?.address?.state?.id;
      return typeof id === 'number' && selectedStates.includes(id);
    })
    : visibleRows);
  // For NR, match by stateCode string to StateRes.value
  const { states } = useStatesFilter();
  const selectedStateValues = states.filter((s) => s.id && selectedStates.includes(s.id)).map((s) => s.value);
  const filteredVisibleNrRows = selectedStates && selectedStates.length > 0
    ? visibleNrRows.filter((x) => x.stateCode && selectedStateValues.includes(x.stateCode))
    : visibleNrRows;

  const driverBreaches = filteredVisibleNrRows.filter((x) => x?.name?.toLowerCase().includes(searchedText.toLowerCase())).filter((x) => Number(x.totalWorkingHours) > 40 || Number(x.actualWorkingHours) > 40 || x.hasWorkedOver12Hours || x.hasWorkedOver5HoursInAnySegment || x.hasBreach);

  const handleNotifyClick = (row: any) => {
    setSelectedRow(row);
    // Pre-fill message with relevant info
    const message = getBreakStatus(row.startedOnActualUTC, row.shiftBreaks, row.endedOnActualUTC).tooltip;
    setNotificationMessage(message);
    setNotifyDialogOpen(true);
  };

  const handleSendNotification = () => {
    showBusyIndicator();
    const shiftWarningReq = {
      message: notificationMessage,
      shiftId: selectedRow?.id
    };

    ShiftsService.warnDriver(shiftWarningReq)
      .then(() => {
        showAlert({ message: 'Notification sent successfully', severity: 'success' });
        setNotifyDialogOpen(false);
        setSelectedRow(null);
        setNotificationMessage('');
      })
      .catch((err: any) => {
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      }).then(() => {
        hideBusyIndicator();
      });
  };

  const formatMsToTime = (ms: number) => {
    if (!ms) return '--';

    const totalMinutes = Math.floor(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const breachColumns = [
    {
      field: 'urgency',
      headerName: 'Message',
      width: 200,
      renderCell: ({ row }: any) => {
        const { tooltip, severity } = getBreakStatus(
          row.startedOnActualUTC,
          row.shiftBreaks,
          row.endedOnActualUTC
        );

        const severityStyles: { [key: string]: { bg: string; text: string } } = {
          high: { bg: '#f8d7da', text: '#721c24' },
          warning: { bg: '#fff3cd', text: '#856404' },
          '': { bg: '#d4edda', text: '#155724' },
        };

        const style = severityStyles[severity] || severityStyles[''];

        return (
          <Tooltip title={tooltip}>
            <span>
              <Chip
                label={tooltip}
                sx={{
                  backgroundColor: style.bg,
                  color: style.text,
                  borderRadius: '12px',
                  padding: '0.5rem',
                  fontWeight: 500,
                  height: 'auto',
                  fontSize: '10px',
                  '& .MuiChip-label': {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    textAlign: 'left',
                    padding: 0
                  }
                }}
              />
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: 'driver',
      headerName: 'Driver',
      width: 150,
      renderCell: ({ row }: any) => (
        <Tooltip title={row.userEmail || ''}>
          <span>{`${row.user.firstName} ${row.user.lastName}`}</span>
        </Tooltip>
      )
    },
    {
      field: 'userPhone',
      headerName: 'Mobile',
      flex: 1,
      minWidth: 100,
      filterable: false,
      hide: false,
      renderHeader: () => (
        <Tooltip title="Mobile number of the assigned driver">
          <span style={{ fontWeight: '600' }}>Mobile</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <Tooltip title={value || ''}>
            <span>{value}</span>
          </Tooltip>
        );
      },
    },
    {
      field: 'scheduledStartDateUTC',
      headerName: 'Date',
      flex: 1,
      minWidth: 100,
      type: 'date',
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Scheduled date of the shift">
          <span style={{ fontWeight: '600' }}>Date</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value, row } = params;
        return (
          <Tooltip title={formatDateToCalendarTime(row.scheduledStartUTC, true) || ''}>
            <span>
              {formatDateToOnlyDate(row.scheduledStartUTC, true)}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'amOrPm',
      headerName: 'Shift',
      minWidth: 10,
      filterable: true,
      renderCell: (params: any) => {
        const { value, row } = params;
        return (
          <Tooltip title={value}>
            <span>{value || '-'}</span>
          </Tooltip>
        );
      }
    },
    {
      field: 'startedOnUTC',
      headerName: 'Actual Start',
      flex: 1,
      minWidth: 100,
      type: 'date',
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Actual time when the shift started">
          <span style={{ fontWeight: '600' }}>Actual Start</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <Tooltip title={formatDateToCalendarTime(value, true) || ''}>
            <span>
              {formatDateToOnlyTime(value, true)}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'endedOnUTC',
      headerName: 'Actual End',
      flex: 1,
      minWidth: 100,
      type: 'date',
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Actual time when the shift ended">
          <span style={{ fontWeight: '600' }}>Actual End</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <Tooltip title={formatDateToCalendarTime(value, true) || ''}>
            <span>
              {value && formatDateToOnlyTime(value, true)}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'totalTimeWorked',
      headerName: 'Total Time Worked',
      width: 150,
      renderCell: ({ value }: any) => <span>{formatMsToTime(value)}</span>
    },
    {
      field: 'totalBreakTime',
      headerName: 'Total Break Time',
      width: 150,
      renderCell: ({ value }: any) => <span>{formatMsToTime(value)}</span>
    },
    {
      field: 'totalDriving',
      headerName: 'Total Driving',
      width: 180,
      renderCell: ({ row }: any) => (
        <span>{row.scheduledKms} km</span>
      )
    },
    {
      field: 'action',
      headerName: 'Warn Driver',
      width: 180,
      renderCell: ({ row }: any) => (
        <Tooltip title="Click to warn" arrow>
          <IconButton
            color="primary"
            aria-label="notify"
            onClick={() => handleNotifyClick(row)}
          >
            <NotificationsIcon />
          </IconButton>
        </Tooltip>
      )
    }
  ];

  const renderEachShiftCard = (row: any) => {
    let shiftType: 'ongoing' | 'completed' | 'scheduled' = 'scheduled';
    if (row.shiftStatus === 'Ongoing') {
      shiftType = 'ongoing';
    } else if (row.shiftStatus === 'Completed') {
      shiftType = 'completed';
    }
    const breach = getBreakStatus(row.startedOnActualUTC, row.shiftBreaks, row.endedOnActualUTC);
    let breachBg = '#d4edda';
    let breachText = '#155724';
    if (breach.severity === 'high') {
      breachBg = '#f8d7da';
      breachText = '#721c24';
    } else if (breach.severity === 'warning') {
      breachBg = '#fff3cd';
      breachText = '#856404';
    }
    const footer = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={breach.tooltip}
            size="small"
            sx={{
              backgroundColor: breachBg,
              color: breachText,
              borderRadius: '12px',
              '& .MuiChip-label': { whiteSpace: 'normal' }
            }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          Scheduled: {formatDateToLongDate(row.scheduledStartUTC, true)}
        </Typography>
      </Box>
    );

    const actionNode = (
      <Tooltip title="Click to notify" arrow>
        <IconButton color="primary" aria-label="notify" size="small" onClick={(e) => { e.stopPropagation(); handleNotifyClick(row); }}>
          <NotificationsIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );

    const shiftForCard = {
      ...row,
      userName: `${row?.user?.firstName ?? ''} ${row?.user?.lastName ?? ''}`.trim() || row.userEmail,
    };

    return (
      <MobileShiftCard key={row.id} shift={shiftForCard} shiftType={shiftType} footer={footer} actionNode={actionNode} />
    );
  };

  const driverColumns = [
    {
      field: 'name',
      headerName: 'Driver',
      flex: 1,
      renderCell: ({ row }: any) => (
        <Tooltip title={row.email || ''}>
          <span>{row.name}</span>
        </Tooltip>
      ),
    },
    {
      field: 'extDriverId',
      headerName: 'CSA ID',
      width: 120,
      renderCell: ({ value }: any) => (
        <span>{value || '—'}</span>
      )
    },
    {
      field: 'totalWorkingHours',
      headerName: 'Total Hours',
      width: 120,
      renderCell: ({ value }: any) => (
        <span>{value} hrs</span>
      )
    },
    {
      field: 'actualWorkingHours',
      headerName: 'Actual Hours',
      width: 120,
      renderCell: ({ value }: any) => (
        <span>{value} hrs</span>
      )
    },
    {
      field: 'hasWorkedOver12Hours',
      headerName: '12+ Hours',
      width: 100,
      renderCell: ({ value }: any) => (
        <Chip
          label={value ? 'Yes' : 'No'}
          color={value ? 'error' : 'default'}
          size="small"
          style={{
            backgroundColor: value ? '#f8d7da' : 'inherit',
            color: value ? '#721c24' : 'inherit',
            borderRadius: '12px',
          }}
        />
      )
    },
    {
      field: 'hasWorkedOver5HoursInAnySegment',
      headerName: '5+ Hours Segment',
      width: 150,
      renderCell: ({ value }: any) => (
        <Chip
          label={value ? 'Yes' : 'No'}
          color={value ? 'error' : 'default'}
          size="small"
          style={{
            backgroundColor: value ? '#f8d7da' : 'inherit',
            color: value ? '#721c24' : 'inherit',
            borderRadius: '12px',
          }}
        />
      )
    },
    {
      field: 'hasBreach',
      headerName: 'Breach',
      width: 100,
      renderCell: ({ value }: any) => (
        <Chip
          label={value ? 'Yes' : 'No'}
          color={value ? 'error' : 'default'}
          size="small"
          style={{
            backgroundColor: value ? '#f8d7da' : 'inherit',
            color: value ? '#721c24' : 'inherit',
            borderRadius: '12px',
          }}
        />
      )
    },
    {
      field: 'hasWorkedOver6PastConsecutiveDays',
      headerName: '6+ Consecutive Days',
      width: 180,
      renderCell: ({ value }: any) => (
        <Chip
          label={value ? 'Yes' : 'No'}
          color={value ? 'error' : 'default'}
          size="small"
          style={{
            backgroundColor: value ? '#f8d7da' : 'inherit',
            color: value ? '#721c24' : 'inherit',
            borderRadius: '12px',
          }}
        />
      )
    }
  ];

  const renderEachDriverCard = (row: any) => (
    <Card key={row.id} sx={{ marginBottom: '10px' }} variant="outlined">
      <CardHeader
        title={row.name}
        subheader={(
          <Chip
            label={`CSA ID: ${row.extDriverId || 'N/A'}`}
            color="primary"
            size="small"
            style={{ borderRadius: '12px' }}
          />
        )}
      />
      <Divider />
      <CardContent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="body2">
              <b>Email:</b> {row.email || '—'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">
              <b>Total Working Hours:</b> {row.totalWorkingHours} hrs
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">
              <b>Actual Working Hours:</b> {row.actualWorkingHours} hrs
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">
              <b>Worked Over 12 Hours:</b>
              <Chip
                label={row.hasWorkedOver12Hours ? 'Yes' : 'No'}
                color={row.hasWorkedOver12Hours ? 'error' : 'default'}
                size="small"
                style={{
                  marginLeft: '8px',
                  backgroundColor: row.hasWorkedOver12Hours ? '#f8d7da' : 'inherit',
                  color: row.hasWorkedOver12Hours ? '#721c24' : 'inherit',
                }}
              />
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">
              <b>5+ Hours in Any Segment:</b>
              <Chip
                label={row.hasWorkedOver5HoursInAnySegment ? 'Yes' : 'No'}
                color={row.hasWorkedOver5HoursInAnySegment ? 'error' : 'default'}
                size="small"
                style={{
                  marginLeft: '8px',
                  backgroundColor: row.hasWorkedOver5HoursInAnySegment ? '#f8d7da' : 'inherit',
                  color: row.hasWorkedOver5HoursInAnySegment ? '#721c24' : 'inherit',
                }}
              />
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">
              <b>Breach Detected:</b>
              <Chip
                label={row.hasBreach ? 'Yes' : 'No'}
                color={row.hasBreach ? 'error' : 'default'}
                size="small"
                style={{
                  marginLeft: '8px',
                  backgroundColor: row.hasBreach ? '#f8d7da' : 'inherit',
                  color: row.hasBreach ? '#721c24' : 'inherit',
                }}
              />
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">
              <b>6+ Consecutive Days:</b>
              <Chip
                label={row.hasWorkedOver6PastConsecutiveDays ? 'Yes' : 'No'}
                color={row.hasWorkedOver6PastConsecutiveDays ? 'error' : 'default'}
                size="small"
                style={{
                  marginLeft: '8px',
                  backgroundColor: row.hasWorkedOver6PastConsecutiveDays ? '#f8d7da' : 'inherit',
                  color: row.hasWorkedOver6PastConsecutiveDays ? '#721c24' : 'inherit',
                }}
              />
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderComplianceIcons = (row: any) => {
    const checkinResult = getCheckinStatus(row.checkins, row.scheduledStartActualUTC);
    const locationResult = getLocationStatus(row.checkins, row.jobsite?.address?.latitude, row.jobsite?.address?.longitude, row.scheduledStartActualUTC);
    const prestartResult = getPrestartStatus(row.checkins);
    const breakResult = getBreakStatus(row.startedOnActualUTC, row.shiftBreaks, row.endedOnActualUTC);

    return (
      <div style={{ display: 'flex', gap: 6 }}>
        <Tooltip title={checkinResult.tooltip} arrow>
          <div style={getIconContainerStyle(checkinResult.status)}>
            <PersonAddIcon style={{ ...iconStyle, color: getIconColor(checkinResult.status) }} />
          </div>
        </Tooltip>
        <Tooltip title={locationResult.tooltip} arrow>
          <div style={getIconContainerStyle(locationResult.status)}>
            <LocationOnIcon style={{ ...iconStyle, color: getIconColor(locationResult.status) }} />
          </div>
        </Tooltip>
        <Tooltip title={prestartResult.tooltip} arrow>
          <div style={getIconContainerStyle(prestartResult.status)}>
            <PlaylistAddCheckIcon style={{ ...iconStyle, color: getIconColor(prestartResult.status) }} />
          </div>
        </Tooltip>
        <Tooltip title={breakResult.tooltip} arrow>
          <div style={getIconContainerStyle(breakResult.status)}>
            <HotelIcon style={{ ...iconStyle, color: getIconColor(breakResult.status) }} />
          </div>
        </Tooltip>
      </div>
    );
  };

  const routeColumns = [
    {
      field: 'scheduledStartDateUTC',
      headerName: 'Date',
      flex: 1,
      minWidth: 100,
      type: 'date',
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Scheduled date of the shift">
          <span style={{ fontWeight: '600' }}>Date</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value, row } = params;
        return (
          <Tooltip title={formatDateToCalendarTime(row.scheduledStartUTC, true) || ''}>
            <span>
              {formatDateToOnlyDate(row.scheduledStartUTC, true)}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'userName',
      headerName: 'Driver',
      width: 150,
      renderCell: ({ row }: any) => (
        <Tooltip title={row.userName || row.userEmail || ''}>
          <span>{row.userName || row.userEmail}</span>
        </Tooltip>
      )
    },
    {
      field: 'amOrPm',
      headerName: 'Shift',
      minWidth: 10,
      filterable: true,
      renderCell: (params: any) => {
        const { value, row } = params;
        return (
          <Tooltip title={value}>
            <span>{value || '-'}</span>
          </Tooltip>
        );
      }
    },
    {
      field: 'compliance',
      headerName: 'TruckSight Compliance',
      flex: 1,
      minWidth: 150,
      renderCell: ({ row }: any) => renderComplianceIcons(row)
    },
    {
      field: 'shift',
      headerName: 'Shift',
      width: 100,
      hide: true,
      renderCell: ({ row }: any) => (
        <Tooltip title={row?.extCompany?.name || ''}>
          <span>{row?.extCompany?.name || '-'}</span>
        </Tooltip>
      )
    },
    {
      field: 'amOrPm',
      headerName: 'Shift',
      width: 100,
      renderCell: ({ row }: any) => (
        <Tooltip title={row.amOrPm || ''}>
          <span>{row.amOrPm || '-'}</span>
        </Tooltip>
      )
    },
    {
      field: 'scheduledActual',
      headerName: 'Dock',
      width: 100,
      renderCell: ({ row }: any) => (
        <Tooltip title={row.dock || ''}>
          <span>{row.dock || '-'}</span>
        </Tooltip>
      )
    },
    {
      field: 'driveHours',
      headerName: 'Status',
      width: 100,
      renderCell: ({ row }: any) => (
        <Tooltip title={row.shiftStatus || ''}>
          <span>{row.shiftStatus || '-'}</span>
        </Tooltip>
      )
    },
    {
      field: 'timeToBreach',
      headerName: 'Rego',
      width: 100,
      renderCell: ({ row }: any) => (
        <Tooltip title={row.vehicle?.registrationNumber || row.vehicleRego || ''}>
          <span>{row.vehicle?.registrationNumber || row.vehicleRego || '—'}</span>
        </Tooltip>
      )
    },
    {
      field: 'scheduledStartUTC',
      headerName: 'Start',
      flex: 1,
      minWidth: 100,
      type: 'date',
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Scheduled start time of the shift">
          <span style={{ fontWeight: '600' }}>Start</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <Tooltip title={formatDateToCalendarTime(value, true) || ''}>
            <span>
              {formatDateToOnlyTime(value, true)}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'scheduledEndUTC',
      headerName: 'End',
      flex: 1,
      minWidth: 100,
      type: 'date',
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Scheduled end time of the shift">
          <span style={{ fontWeight: '600' }}>End</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <Tooltip title={formatDateToCalendarTime(value, true) || ''}>
            <span>
              {value && formatDateToOnlyTime(value, true)}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'startedOnUTC',
      headerName: 'Actual Start',
      flex: 1,
      minWidth: 100,
      type: 'date',
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Actual time when the shift started">
          <span style={{ fontWeight: '600' }}>Actual Start</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <Tooltip title={formatDateToCalendarTime(value, true) || ''}>
            <span>
              {formatDateToOnlyTime(value, true)}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'endedOnUTC',
      headerName: 'Actual End',
      flex: 1,
      minWidth: 100,
      type: 'date',
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Actual time when the shift ended">
          <span style={{ fontWeight: '600' }}>Actual End</span>
        </Tooltip>
      ),
      renderCell: (params: any) => {
        const { value } = params;
        return (
          <Tooltip title={formatDateToCalendarTime(value, true) || ''}>
            <span>
              {value && formatDateToOnlyTime(value, true)}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'userExtDriverId',
      headerName: 'CSA ID',
      width: 120,
      filterable: false
    },
    {
      field: 'scheduledDrops',
      headerName: 'Drops',
      width: 120
    },
    {
      field: 'routePerformanceWeekEnding',
      headerName: 'Route Performance Week Ending',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceOnTimeOrders',
      headerName: 'Route Performance On Time Orders',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceOrders',
      headerName: 'Route Performance Orders',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformancePlannedReload',
      headerName: 'Route Performance Planned Reload',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceOtdPercentage',
      headerName: 'Route Performance OTD Percentage',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceOveragePercentage',
      headerName: 'Route Performance Overage Percentage',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceLateArrivalMinsPerRoute',
      headerName: 'Route Performance Late Arrival Mins Per Route',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceDwellPerRoute',
      headerName: 'Route Performance Dwell Per Route',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceUnloadingPerRoute',
      headerName: 'Route Performance Unloading Per Route',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceWGOIdlePerRoute',
      headerName: 'Route Performance WGO Idle Per Route',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceMicroliseCustomerIdlePerRoute',
      headerName: 'Route Performance Microlise Customer Idle Per Route',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceMicroliseEnrouteIdlePerRoute',
      headerName: 'Route Performance Microlise Enroute Idle Per Route',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceMicroliseIgnitionOffOutsideGeofence',
      headerName: 'Route Performance Microlise Ignition Off Outside Geofence',
      width: 350,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceTotalIdleTimePerRoute',
      headerName: 'Route Performance Total Idle Time Per Route',
      width: 300,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceCheckoutOutsideGeofencePerRoute',
      headerName: 'Route Performance Checkout Outside Geofence Per Route',
      width: 350,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceServiceAndUnknownOveragePerRoute',
      headerName: 'Route Performance Service And Unknown Overage Per Route',
      width: 350,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    },
    {
      field: 'routePerformanceDriveAndUnknownOveragePerRoute',
      headerName: 'Route Performance Drive And Unknown Overage Per Route',
      width: 350,
      renderCell: ({ value }: any) => <span>{value ?? '—'}</span>,
    }
  ];

  const renderEachRouteCard = (row: any) => {
    let shiftType: 'ongoing' | 'completed' | 'scheduled' = 'scheduled';
    if (row.shiftStatus === 'Ongoing') {
      shiftType = 'ongoing';
    } else if (row.shiftStatus === 'Completed') {
      shiftType = 'completed';
    }

    const footer = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {renderComplianceIcons(row)}
          </Box>
          <Chip label={row.shiftStatus || '-'} size="small" variant="outlined" />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">Dock: {row.dock || '-'}</Typography>
          <Typography variant="caption" color="text.secondary">Vehicle: {row.vehicle?.registrationNumber || row.vehicleRego || '—'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">Start: {formatDateToOnlyTime(row.scheduledStartUTC, true)}</Typography>
          <Typography variant="caption" color="text.secondary">End: {row.scheduledEndUTC && formatDateToOnlyTime(row.scheduledEndUTC, true)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">Timesheet: {row.timesheetStatus || 'No'}</Typography>
          <Typography variant="caption" color="text.secondary">Drops: {row.scheduledDrops || '0'}</Typography>
        </Box>
      </Box>
    );

    return (
      <MobileShiftCard key={row.id} shift={row} shiftType={shiftType} footer={footer} />
    );
  };

  const theme = useTheme();

  const onSearchTextChange = (event: any) => {
    filterByText(event?.target?.value);
  };

  const onRowClick = ({ id, extCompany }: any) => {
    window.open(`/app/shift/list/${id}`, '_blank');
  };

  const onTimesheetRowClick = ({ id }: any) => {
    window.open(`/app/linfox/${id}`, '_blank');
  };

  const handleDateRangeChange = (start: string, end: string) => {
    if (start && end) {
      fetchAllRows(start, end);
    }
    onDateRangeChange(companyId, start, end);
  };

  const transformDriverDataToDailyHours = (items: FormattedShiftNR[]) => {
    const dailyHours: { [key: string]: number } = {};

    // Sum up hours by date
    items.forEach((item) => {
      console.log(item);
      const date = (item.date) || ''; // or item.dateLocal.split('T')[0] if you prefer ISO format
      const hours = parseFloat(item.totalWorkingHours || '0');

      if (!dailyHours[date]) {
        dailyHours[date] = 0;
      }
      dailyHours[date] += hours;
    });

    // Convert to array format for Recharts
    return Object.keys(dailyHours).map((date) => ({
      date,
      totalHours: parseFloat(dailyHours[date].toFixed(2)) // Round to 2 decimal places
    }));
  };

  const handleNotifyDialogClose = () => {
    setNotifyDialogOpen(false);
    setSelectedRow(null);
    setNotificationMessage('');
  };

  return (
    <>
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          alignItems: 'center',
          gap: 2,
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <GlobalSearch
          searchedText={searchedText}
          onSearchTextChange={onSearchTextChange}
          showFilterByColumn={false}
          width="150px"
        />
        {(() => {
          const countsData = [
            { label: 'Total', value: filteredVisibleRows.length, color: '#7E57C2', icon: <NumbersIcon style={{ color: '#7E57C2' }} fontSize="small" /> },
            { label: 'Scheduled', value: filteredVisibleRows.filter((x) => x.shiftStatus === 'Scheduled').length, color: '#FF7043', icon: <ScheduleIcon style={{ color: '#FF7043' }} fontSize="small" /> },
            { label: 'Accepted', value: filteredVisibleRows.filter((x) => x.userHasAccepted).length, color: '#26A69A', icon: <CheckCircleOutlineIcon style={{ color: '#26A69A' }} fontSize="small" /> },
            { label: 'Ongoing', value: filteredVisibleRows.filter((x) => x.shiftStatus === 'Ongoing').length, color: '#FFCA28', icon: <HourglassBottomIcon style={{ color: '#FFCA28' }} fontSize="small" /> },
            { label: 'Completed', value: filteredVisibleRows.filter((x) => x.shiftStatus === 'Completed').length, color: '#66BB6A', icon: <TaskAltIcon style={{ color: '#66BB6A' }} fontSize="small" /> },
            { label: 'Timesheets', value: filteredVisibleRows.filter((x) => x.timesheetStatus === 'Yes').length, color: '#5C6BC0', icon: <DescriptionIcon style={{ color: '#5C6BC0' }} fontSize="small" /> },
            { label: 'AM', value: filteredVisibleRows.filter((x) => x.amOrPm === 'AM').length, color: '#42A5F5', icon: <ScheduleIcon style={{ color: '#42A5F5' }} fontSize="small" /> },
            { label: 'PM', value: filteredVisibleRows.filter((x) => x.amOrPm === 'PM').length, color: '#EF5350', icon: <ScheduleIcon style={{ color: '#EF5350' }} fontSize="small" /> },
          ];

          return (
            <>
              <Hidden mdDown>
                <Box
                  sx={{
                    mt: 3,
                    mb: 3,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1.5,
                    position: 'relative',
                  }}
                >
                  {countsData.map((item, index) => (
                    <Chip
                      key={index}
                      clickable
                      onClick={() => setSelectedSummary(item.label)}
                      onDelete={selectedSummary === item.label && item.label !== 'Total' ? () => setSelectedSummary('') : undefined}
                      deleteIcon={selectedSummary === item.label && item.label !== 'Total' ? <CancelIcon fontSize="small" /> : undefined}
                      label={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {item.icon}
                          <Box>
                            <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                              {item.value}
                            </Typography>
                            <Typography
                              variant="body2"
                              component="span"
                              sx={{ ml: 0.5, fontWeight: selectedSummary === item.label ? 700 : 400, cursor: 'pointer' }}
                            >
                              {item.label}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      sx={{
                        height: 36,
                        borderRadius: 18,
                        bgcolor: `${item.color}4D`,
                        border: `1px solid ${item.color}80`,
                        '& .MuiChip-label': { px: 1.5, py: 0.5 },
                        '&:hover': { bgcolor: `${item.color}12`, transform: 'translateY(-1px)' },
                        transition: 'all 0.2s ease',
                        boxShadow: selectedSummary === item.label ? `0 0 0 2px ${item.color}80` : undefined,
                        fontWeight: selectedSummary === item.label ? 700 : 400,
                      }}
                      variant={selectedSummary === item.label ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
              </Hidden>

              <Hidden mdUp>
                <Grid container spacing={1.5} sx={{ mt: 2, mb: 2, width: '100%' }}>
                  {countsData.map((item) => {
                    const isSelected = selectedSummary === item.label;
                    const alphaBg = `${item.color}14`;
                    const alphaBorder = `${item.color}40`;
                    return (
                      <Grid item xs={6} key={item.label}>
                        <Card
                          elevation={isSelected ? 3 : 0}
                          onClick={() => setSelectedSummary(isSelected && item.label !== 'Total' ? '' : item.label)}
                          sx={{
                            cursor: 'pointer',
                            borderRadius: 2,
                            border: `1px solid ${alphaBorder}`,
                            background: alphaBg,
                            transition: 'all .2s ease',
                            '&:active': { transform: 'scale(0.995)' },
                            boxShadow: isSelected ? `0 0 0 2px ${alphaBorder}` : 'none'
                          }}
                        >
                          <CardContent sx={{ p: '10px !important' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: `${item.color}26`
                                }}
                              >
                                {item.icon}
                              </Box>
                              <Box sx={{ ml: 0.5 }}>
                                <Typography variant="subtitle1" sx={{ lineHeight: 1.1, fontWeight: 700, color: '#161725' }}>
                                  {item.value}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  {item.label}
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Hidden>
            </>
          );
        })()}

        <DateRangeSelector
          id="shifts"
          startDate={startDate}
          endDate={endDate}
          onChange={handleDateRangeChange}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
          {StatesDropdown}
          {/* {process.env.NODE_ENV === 'development' && (
            <Tooltip title={testData ? 'Clear test data' : 'Upload test data'}>
              <IconButton
                size="small"
                onClick={() => {
                  if (testData) {
                    clearTestData();
                  } else {
                    setShowUploadDialog(true);
                  }
                }}
                sx={{
                  bgcolor: testData ? '#ff4444' : '#4caf50',
                  color: 'white',
                  '&:hover': {
                    bgcolor: testData ? '#ff0000' : '#45a049'
                  }
                }}
              >
                {testData ? <CancelIcon /> : <DescriptionIcon />}
              </IconButton>
            </Tooltip>
          )} */}
        </Box>
      </Box>
      <Dialog
        open={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Upload Test Data</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Upload a JSON file to use as test data instead of live data.
          </Typography>
          <Button
            variant="outlined"
            component="label"
            fullWidth
          >
            Choose JSON File
            <input
              type="file"
              hidden
              accept=".json"
              onChange={handleFileUpload}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUploadDialog(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={notifyDialogOpen}
        onClose={() => setNotifyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Warning to {selectedRow?.user?.firstName} {selectedRow?.user?.lastName}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={6}
            value={notificationMessage}
            onChange={(e) => setNotificationMessage(e.target.value)}
            fullWidth
            variant="outlined"
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotifyDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleSendNotification}
            color="primary"
            variant="contained"
            disabled={!notificationMessage.trim()}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          marginTop: 2,
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip
            icon={<ErrorIcon style={{ color: '#d32f2f' }} fontSize="small" />}
            label={(
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                  {filteredVisibleRows.filter((x) => getBreakStatus(x.startedOnActualUTC, x.shiftBreaks, x.endedOnActualUTC).severity === 'high' || getBreakStatus(x.startedOnActualUTC, x.shiftBreaks, x.endedOnActualUTC).status === 'red').length}
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    ml: 0.5,
                    fontWeight: selectedSummary === 'Breached' ? 700 : 400,
                    cursor: 'pointer',
                  }}
                >
                  Breached
                </Typography>
              </Box>
            )}
            clickable
            onClick={() => setSelectedSummary('Breached')}
            onDelete={selectedSummary === 'Breached' ? () => setSelectedSummary('') : undefined}
            deleteIcon={selectedSummary === 'Breached' ? <CancelIcon fontSize="small" /> : undefined}
            sx={{
              height: 36,
              borderRadius: 18,
              border: 'none',
              transition: 'all 0.2s ease',
              fontWeight: selectedSummary === 'Breached' ? 700 : 400,
            }}
            variant={selectedSummary === 'Breached' ? 'filled' : 'outlined'}
          />
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip
            icon={<ReportProblemIcon style={{ color: '#ffa000' }} fontSize="small" />}
            label={(
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                  {filteredVisibleRows.filter((x) => getBreakStatus(x.startedOnActualUTC, x.shiftBreaks, x.endedOnActualUTC).severity === 'warning').length}
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    ml: 0.5,
                    fontWeight: selectedSummary === 'High Risk' ? 700 : 400,
                    cursor: 'pointer',
                  }}
                >
                  At Risk of Breach
                </Typography>
              </Box>
            )}
            clickable
            onClick={() => setSelectedSummary('High Risk')}
            onDelete={selectedSummary === 'High Risk' ? () => setSelectedSummary('') : undefined}
            deleteIcon={selectedSummary === 'High Risk' ? <CancelIcon fontSize="small" /> : undefined}
            sx={{
              height: 36,
              borderRadius: 18,
              border: 'none',
              transition: 'all 0.2s ease',
              fontWeight: selectedSummary === 'High Risk' ? 700 : 400,
            }}
            variant={selectedSummary === 'High Risk' ? 'filled' : 'outlined'}
          />
        </Stack>
      </Box>

      <Typography variant="h3" gutterBottom sx={{ color: '#161725', mt: 2, mb: 2 }}>
        Breaches by Shifts
      </Typography>

      {/* Filter rows by selected summary chip, including AM/PM, Breached, and High Risk */}
      {(() => {
        let filteredRows = filteredVisibleRows.slice().reverse();
        if (selectedSummary === 'Scheduled') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.shiftStatus === 'Scheduled');
        } else if (selectedSummary === 'Accepted') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.userHasAccepted);
        } else if (selectedSummary === 'Ongoing') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.shiftStatus === 'Ongoing');
        } else if (selectedSummary === 'Completed') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.shiftStatus === 'Completed');
        } else if (selectedSummary === 'Timesheets') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.timesheetStatus === 'Yes');
        } else if (selectedSummary === 'AM') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.amOrPm === 'AM');
        } else if (selectedSummary === 'PM') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.amOrPm === 'PM');
        } else if (selectedSummary === 'Breached') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => getBreakStatus(x.startedOnActualUTC, x.shiftBreaks, x.endedOnActualUTC).severity === 'high' || getBreakStatus(x.startedOnActualUTC, x.shiftBreaks, x.endedOnActualUTC).status === 'red');
        } else if (selectedSummary === 'High Risk') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => getBreakStatus(x.startedOnActualUTC, x.shiftBreaks, x.endedOnActualUTC).severity === 'warning');
        }
        // For the breaches table, always show only breaches (red or high severity or warning)
        const breaches = filteredRows.filter(
          (x) => getBreakStatus(x.startedOnActualUTC, x.shiftBreaks, x.endedOnActualUTC).status === 'red'
            || getBreakStatus(x.startedOnActualUTC, x.shiftBreaks, x.endedOnActualUTC).severity === 'high'
            || getBreakStatus(x.startedOnActualUTC, x.shiftBreaks, x.endedOnActualUTC).severity === 'warning'
        );
        if (breaches.length === 0) {
          return (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#f5f5f5',
                padding: 2,
                borderRadius: 1,
                textAlign: 'center',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontStyle: 'italic',
                }}
              >
                No shift breaches.
              </Typography>
            </Box>
          );
        }
        return (
          <>
            <Hidden mdDown>
              <Table
                rows={breaches}
                columns={breachColumns}
                onRowDoubleClick={({ row }) => onRowClick(row)}
                page={page}
                pageSize={5}
                totalRows={breaches.length}
                sortModel={sortModel}
                onPageChange={(newPage) => setPage(newPage)}
                onSortModelChange={(model) => setSortModel(model)}
              />
            </Hidden>
            <Hidden mdUp>
              <MobileDataList
                rows={breaches}
                renderEachRow={renderEachShiftCard}
                totalRows={breaches.length}
                page={page}
                onPageChange={(newPage: any) => setPage(newPage)}
              />
            </Hidden>
          </>
        );
      })()}

      <Box
        sx={{ mt: 2 }}
      />

      <Typography variant="h3" gutterBottom sx={{ color: '#161725', mt: 4, mb: 2 }}>
        Breaches by Timesheet
      </Typography>

      {driverBreaches.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#f5f5f5', // light grey
            padding: 2,
            borderRadius: 1,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontStyle: 'italic',
            }}
          >
            No timesheet breaches.
          </Typography>
        </Box>
      ) : (
        <>
          <Hidden mdDown>
            <Table
              rows={driverBreaches}
              columns={driverColumns}
              onRowDoubleClick={({ row }) => onTimesheetRowClick(row)}
              page={page}
              totalRows={driverBreaches.length}
              sortModel={sortModel}
              onPageChange={(newPage) => setPage(newPage)}
              onSortModelChange={(model) => setSortModel(model)}
            />
          </Hidden>

          <Hidden mdUp>
            <MobileDataList
              rows={driverBreaches}
              renderEachRow={renderEachDriverCard}
              totalRows={driverBreaches.length}
              page={page}
              onPageChange={(newPage: any) => setPage(newPage)}
            />
          </Hidden>
        </>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 4, mb: 2 }}>
        <Typography variant="h3" gutterBottom sx={{ color: '#161725', mr: 2 }}>
          All Routes
        </Typography>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="rows-per-page-label">Rows per page</InputLabel>
          <Select
            labelId="rows-per-page-label"
            id="rows-per-page-select"
            value={rowsPerPage}
            label="Rows per page"
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={30}>30</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Filter routes by selected summary chip, including AM/PM */}
      {(() => {
        let filteredRows = filteredVisibleRows.slice().reverse();
        if (selectedSummary === 'Scheduled') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.shiftStatus === 'Scheduled');
        } else if (selectedSummary === 'Accepted') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.userHasAccepted);
        } else if (selectedSummary === 'Ongoing') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.shiftStatus === 'Ongoing');
        } else if (selectedSummary === 'Completed') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.shiftStatus === 'Completed');
        } else if (selectedSummary === 'Timesheets') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.timesheetStatus === 'Yes');
        } else if (selectedSummary === 'AM') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.amOrPm === 'AM');
        } else if (selectedSummary === 'PM') {
          filteredRows = filteredVisibleRows.slice().reverse().filter((x) => x.amOrPm === 'PM');
        }
        return (
          <>
            <Hidden mdDown>
              <Table
                rows={filteredRows}
                onRowDoubleClick={({ row }) => onRowClick(row)}
                columns={routeColumns}
                page={page}
                pageSize={rowsPerPage}
                totalRows={filteredRows.length}
                sortModel={sortModel}
                onPageChange={(newPage) => setPage(newPage)}
                onSortModelChange={(model) => setSortModel(model)}
              />
            </Hidden>
            <Hidden mdUp>
              <MobileDataList
                rows={filteredRows}
                renderEachRow={renderEachRouteCard}
                totalRows={filteredRows.length}
                page={page}
                onPageChange={(newPage: any) => setPage(newPage)}
              />
            </Hidden>
          </>
        );
      })()}

      <Typography variant="h3" gutterBottom sx={{ color: '#161725', mt: 4, mb: 2 }}>
        Daily Driver Working Hours
      </Typography>

      <Card
        sx={{
          borderRadius: '0',
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          p: 2,
          height: '100%'
        }}
      >
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={transformDriverDataToDailyHours(visibleNrRows)}
              margin={{ top: 30, right: 0, left: 0, bottom: 30 }}
            >
              <defs>
                <linearGradient id="colorHours" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#5664d2" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#5664d2" stopOpacity={1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" opacity={0.6} />
              <XAxis
                dataKey="date"
                angle={-45}
                textAnchor="end"
                height={60}
                tick={{ fill: '#6b7280' }}
              />
              <YAxis
                tick={{ fill: '#3f51b5' }}
                label={{ value: 'Hours', angle: -90, position: 'insideLeft', fill: '#3f51b5' }}
              />
              <RechartsTooltip
                contentStyle={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                }}
                formatter={(value: any) => [`${value} hours`, 'Total Hours']}
              />
              <Bar
                dataKey="totalHours"
                name="Total Hours"
                radius={[4, 4, 0, 0]}
                fill="url(#colorHours)"
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  visibleRows: state.shift?.dashboard?.filteredRows || [],
  visibleNrRows: state.dashboardNR?.allRows || [],
  startDate: state.shift?.weekStartDate || '',
  endDate: state.shift?.weekEndDate || '',
  companyId: state.profile?.company?.id,
  searchedText: state.shift?.dashboard?.searchedText,
  filterByCol: state.shift?.dashboard?.filterByCol
});

const mapDispatchToProps = (dispatch: any) => ({
  filterByText: (text: string) => dispatch(searchByTextDashboard(text)),
  onChangeFilterByColumn: (col: any) => dispatch(changeFilterByColumnDashboard(col)),
  onDateRangeChange: (companyId: number, startDate: string, endDate: string) => {
    dispatch(changeDateRange(startDate, endDate));
    dispatch(changeWeekDateRange(startDate, endDate));
    dispatch(changeDateRangeNr(startDate, endDate));
    dispatch(refreshDataNr(companyId, startDate, endDate));
  },
  fetchAllRows: (startDate: string, endDate: string) => dispatch(refreshDataWithDateRange(startDate, endDate)),
  showBusyIndicator: () => dispatch(show()),
  hideBusyIndicator: () => dispatch(hide()),
  showAlert: (message: any) => dispatch(showGlobalAlert(message)),
  clearAllAlerts: () => dispatch(clearAllGlobalAlerts())
});

export default connect(mapStateToProps, mapDispatchToProps)(DashboardFatigue);
